import { type ApolloCache, type Reference, type StoreObject } from '@apollo/client';

interface CacheEntity extends StoreObject {
    id: string;
    __typename: string;
}

export function setCachedField(
    cache: ApolloCache<unknown>,
    entity: CacheEntity,
    field: string,
    value: unknown,
): void {
    const cacheId = cache.identify(entity);
    if (!cacheId) return;
    cache.modify({
        id: cacheId,
        fields: { [field]: () => value },
    });
}

export function setCachedFields(
    cache: ApolloCache<unknown>,
    entity: CacheEntity,
    fields: Record<string, unknown>,
): void {
    const cacheId = cache.identify(entity);
    if (!cacheId) return;

    const fieldModifiers = Object.fromEntries(
        Object.entries(fields)
            .filter(([field]) => field !== '__typename')
            .map(([field, value]) => [field, () => value]),
    );

    cache.modify({
        id: cacheId,
        fields: fieldModifiers,
    });
}

export function appendRefToField(
    cache: ApolloCache<unknown>,
    parent: CacheEntity,
    field: string,
    child: CacheEntity,
): void {
    const childRef = cache.identify(child);
    if (!childRef) return;
    cache.modify({
        id: cache.identify(parent),
        fields: {
            [field]: (existingRefs: readonly Reference[] = []) => [
                ...existingRefs,
                { __ref: childRef },
            ],
        },
    });
}

export function removeRefFromField(
    cache: ApolloCache<unknown>,
    parent: CacheEntity,
    field: string,
    childId: string,
): void {
    const parentCacheId = cache.identify(parent);
    if (!parentCacheId) return;
    cache.modify({
        id: parentCacheId,
        fields: {
            [field]: (
                existingRefs: readonly Reference[] | undefined,
                { readField },
            ) =>
                (existingRefs ?? []).filter(
                    (ref) => readField('id', ref) !== childId,
                ),
        },
    });
}

export function evictAndGc(
    cache: ApolloCache<unknown>,
    entity: CacheEntity,
): void {
    cache.evict({ id: cache.identify(entity) });
    cache.gc();
}
